import type {
	DocCursor,
	DocPeerData,
	DocPoint,
	DocViewer,
} from "@productlane/lib"
import { DOC_TYPING_TTL_MS } from "@productlane/lib"

export interface DocPeer {
	viewer: DocViewer
	typingAt: number | null
	cursor: DocCursor | null
}

/** Peers in one doc, keyed by socket id so two tabs of one person stay apart. */
export type DocPeers = Record<string, DocPeer>

export interface RemoteCursor {
	socketId: string
	viewer: DocViewer
	cursor: DocCursor
}

const samePoint = (a: DocPoint, b: DocPoint) =>
	a.block === b.block &&
	a.offset === b.offset &&
	a.text === b.text &&
	a.blocks === b.blocks &&
	a.before === b.before &&
	a.line === b.line &&
	a.lineOffset === b.lineOffset

const sameCursor = (a: DocCursor | null, b: DocCursor | null) =>
	a === b ||
	(!!a && !!b && samePoint(a.anchor, b.anchor) && samePoint(a.head, b.head))

/**
 * Records what a peer just sent; an arrival keeps nothing it had not said yet.
 * A repeat of what is already stored returns the same object, so nothing re-renders.
 */
export function applyPeer(
	peers: DocPeers,
	data: DocPeerData,
	now: number,
): DocPeers {
	const current = peers[data.socketId]
	if (
		current &&
		!data.typing &&
		current.typingAt === null &&
		current.viewer.userId === data.viewer.userId &&
		current.viewer.name === data.viewer.name &&
		current.viewer.imageUrl === data.viewer.imageUrl &&
		sameCursor(current.cursor, data.cursor ?? null)
	) {
		return peers
	}
	return {
		...peers,
		[data.socketId]: {
			viewer: data.viewer,
			typingAt: data.typing ? now : null,
			cursor: data.cursor ?? null,
		},
	}
}

export function removePeer(peers: DocPeers, socketId: string): DocPeers {
	if (!(socketId in peers)) return peers
	const { [socketId]: _removed, ...rest } = peers
	return rest
}

/** Everyone else with the doc open, once per person, in the order they arrived. */
export function otherViewers(
	peers: DocPeers,
	selfUserId?: string,
): DocViewer[] {
	const seen = new Map<string, DocViewer>()
	for (const { viewer } of Object.values(peers)) {
		if (viewer.userId === selfUserId || seen.has(viewer.userId)) continue
		seen.set(viewer.userId, viewer)
	}
	return [...seen.values()]
}

/** Teammates who typed within the TTL, so a lost "stopped" message never sticks. */
export function typingViewers(
	peers: DocPeers,
	selfUserId: string | undefined,
	now: number,
): DocViewer[] {
	const typing = Object.fromEntries(
		Object.entries(peers).filter(
			([, peer]) =>
				peer.typingAt !== null && now - peer.typingAt < DOC_TYPING_TTL_MS,
		),
	)
	return otherViewers(typing, selfUserId)
}

/** Other people's carets, one per tab, without the reader's own tabs. */
export function remoteCursors(
	peers: DocPeers,
	selfUserId: string | undefined,
): RemoteCursor[] {
	return Object.entries(peers).flatMap(([socketId, peer]) =>
		peer.cursor && peer.viewer.userId !== selfUserId
			? [{ socketId, viewer: peer.viewer, cursor: peer.cursor }]
			: [],
	)
}
